/**
 * React Hooks for IndexedDB Operations
 *
 * Provides React hooks for accessing and mutating IndexedDB data
 * with automatic state management and error handling
 *
 * @module lib/hooks/use-db
 */

'use client';

import { useState, useEffect, useCallback } from 'react';
import {
  db,
  jobOperations,
  inspectionOperations,
  photoOperations,
  syncQueueOperations,
  metadataOperations,
  type Job,
  type Inspection,
  type Photo,
  type SyncQueueItem,
} from '../db';

// ============================================================================
// Database Stats Hook
// ============================================================================

export function useDBStats() {
  const [stats, setStats] = useState({
    jobs: 0,
    inspections: 0,
    photos: 0,
    syncQueue: 0,
    total: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const newStats = await db.getStats();
      setStats(newStats);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { stats, loading, error, refresh };
}

// ============================================================================
// Jobs Hooks
// ============================================================================

export function useJobs(userId: string) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await jobOperations.getAllForUser(userId);
      setJobs(data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { jobs, loading, error, refresh };
}

export function useJob(jobId: string) {
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await jobOperations.getById(jobId);
      setJob(data || null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const updateJob = useCallback(
    async (updates: Partial<Job>) => {
      try {
        await jobOperations.update(jobId, updates);
        await refresh();
      } catch (err) {
        setError(err as Error);
        throw err;
      }
    },
    [jobId, refresh]
  );

  return { job, loading, error, refresh, updateJob };
}

export function useJobsByStatus(userId: string, status: Job['status']) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await jobOperations.getByStatus(userId, status);
      setJobs(data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [userId, status]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { jobs, loading, error, refresh };
}

// ============================================================================
// Inspections Hooks
// ============================================================================

export function useInspection(inspectionId: string) {
  const [inspection, setInspection] = useState<Inspection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [completion, setCompletion] = useState(0);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await inspectionOperations.getById(inspectionId);
      setInspection(data || null);

      if (data) {
        const completionPercent = await inspectionOperations.getCompletionPercentage(
          inspectionId
        );
        setCompletion(completionPercent);
      }
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [inspectionId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const updateInspection = useCallback(
    async (updates: Partial<Inspection>) => {
      try {
        await inspectionOperations.update(inspectionId, updates);
        await refresh();
      } catch (err) {
        setError(err as Error);
        throw err;
      }
    },
    [inspectionId, refresh]
  );

  const updateSection = useCallback(
    async (sectionId: string, updates: Partial<Record<string, unknown>>) => {
      try {
        await inspectionOperations.updateSection(inspectionId, sectionId, updates);
        await refresh();
      } catch (err) {
        setError(err as Error);
        throw err;
      }
    },
    [inspectionId, refresh]
  );

  const addPhotoToSection = useCallback(
    async (sectionId: string, photoId: string) => {
      try {
        await inspectionOperations.addPhotoToSection(inspectionId, sectionId, photoId);
        await refresh();
      } catch (err) {
        setError(err as Error);
        throw err;
      }
    },
    [inspectionId, refresh]
  );

  return {
    inspection,
    loading,
    error,
    completion,
    refresh,
    updateInspection,
    updateSection,
    addPhotoToSection,
  };
}

export function useInspectionByJob(jobId: string) {
  const [inspection, setInspection] = useState<Inspection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await inspectionOperations.getByJobId(jobId);
      setInspection(data || null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { inspection, loading, error, refresh };
}

// ============================================================================
// Photos Hooks
// ============================================================================

export function usePhotos(inspectionId: string) {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await photoOperations.getByInspection(inspectionId);
      setPhotos(data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [inspectionId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { photos, loading, error, refresh };
}

export function usePhotosBySection(inspectionId: string, sectionId: string) {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await photoOperations.getBySection(inspectionId, sectionId);
      setPhotos(data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [inspectionId, sectionId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addPhoto = useCallback(
    async (photo: Photo) => {
      try {
        await photoOperations.create(photo);
        await refresh();
      } catch (err) {
        setError(err as Error);
        throw err;
      }
    },
    [refresh]
  );

  const deletePhoto = useCallback(
    async (photoId: string) => {
      try {
        await photoOperations.delete(photoId);
        await refresh();
      } catch (err) {
        setError(err as Error);
        throw err;
      }
    },
    [refresh]
  );

  return { photos, loading, error, refresh, addPhoto, deletePhoto };
}

export function useUnuploadedPhotos() {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await photoOperations.getUnuploaded();
      setPhotos(data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const markUploaded = useCallback(
    async (photoId: string, cloudPath: string) => {
      try {
        await photoOperations.markUploaded(photoId, cloudPath);
        await refresh();
      } catch (err) {
        setError(err as Error);
        throw err;
      }
    },
    [refresh]
  );

  return { photos, loading, error, refresh, markUploaded };
}

// ============================================================================
// Sync Queue Hooks
// ============================================================================

export function useSyncQueue() {
  const [queue, setQueue] = useState<SyncQueueItem[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [pending, failed, pendingCountValue, failedCountValue] = await Promise.all([
        syncQueueOperations.getPending(),
        syncQueueOperations.getFailed(),
        syncQueueOperations.getCount('pending'),
        syncQueueOperations.getCount('failed'),
      ]);

      setQueue([...pending, ...failed]);
      setPendingCount(pendingCountValue);
      setFailedCount(failedCountValue);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addToQueue = useCallback(
    async (item: Omit<SyncQueueItem, 'id' | 'created_at'>) => {
      try {
        await syncQueueOperations.add(item);
        await refresh();
      } catch (err) {
        setError(err as Error);
        throw err;
      }
    },
    [refresh]
  );

  const clearCompleted = useCallback(async () => {
    try {
      await syncQueueOperations.cleanCompleted();
      await refresh();
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  }, [refresh]);

  return {
    queue,
    pendingCount,
    failedCount,
    loading,
    error,
    refresh,
    addToQueue,
    clearCompleted,
  };
}

// ============================================================================
// Metadata Hook
// ============================================================================

export function useMetadata<T = unknown>(key: string, defaultValue?: T) {
  const [value, setValue] = useState<T | undefined>(defaultValue);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await metadataOperations.get<T>(key);
      setValue(data ?? defaultValue);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [key, defaultValue]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const update = useCallback(
    async (newValue: T) => {
      try {
        await metadataOperations.set(key, newValue);
        setValue(newValue);
      } catch (err) {
        setError(err as Error);
        throw err;
      }
    },
    [key]
  );

  return { value, loading, error, update, refresh };
}

// ============================================================================
// Last Sync Hook
// ============================================================================

export function useLastSync() {
  const [lastSync, setLastSync] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await metadataOperations.getLastSync();
      setLastSync(data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const updateLastSync = useCallback(async () => {
    try {
      await metadataOperations.updateLastSync();
      await refresh();
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  }, [refresh]);

  return { lastSync, loading, error, updateLastSync, refresh };
}
